import mediaObjectApi from "@apis/media.object.api";
import CommonUtils from "../utils/common.utils.ts";
import { FileResponse } from "@plugins/file-uploader-v2/types.ts";
import { DOWNLOADABLE_RIGHT } from "@plugins/media/media.constant";
import { MediaObjectResponse } from "@plugins/media/types.ts";
import { MEDIA_OBJECT_FILE_STATUS } from "../richtext.constant.ts";
import { ApiResponse } from "@/apis/api.ts";
import { isMediaObjectFileReady } from "@plugins/media/media.utils.ts";

export default class MediaObjectService {
  constructor(context) {
    this.context = context;
  }

  create(
    fileResponse: FileResponse,
    markable?: boolean,
    sourceMediaObjectUuid?: string,
  ): Promise<MediaObjectResponse> {
    return new Promise<MediaObjectResponse>((resolve, reject) => {
      const requestData = {
        contextUuid: this.context.resourceUuid ? this.context.resourceUuid : null,
        contextType: this.context.resourceType ? this.context.resourceType : null,
        uploadFrom: this.context.uploadFrom,
        fileEntry: {
          fileUuid: fileResponse.uuid,
          filename: fileResponse.name,
        },
        mediaObjectCopyRightModel: {
          downloadableRight: DOWNLOADABLE_RIGHT.YES.value, // SLS-10590 - always downloadable
        },
        resourceDomain: this.context.resourceDomain,
        ownerGroupId: this.context.ownerGroupId,
        markable: markable || false,
        sourceMediaObjectUuid,
      };
      mediaObjectApi.createMediaObject(requestData).then(
        //OK
        (response) => {
          if (response.code === "error") {
            reject(response);
          }
          resolve(response.data);
        },
        (response) => {
          reject(response);
        },
      );
    });
  }

  async preCreate(): Promise<string> {
    const requestData = {
      contextUuid: this.context.resourceUuid ? this.context.resourceUuid : null,
      contextType: this.context.resourceType ? this.context.resourceType : null,
      uploadFrom: this.context.uploadFrom,
      resourceDomain: this.context.resourceDomain,
      ownerGroupId: this.context.ownerGroupId,
      title: "Untitled", //temp
    };
    const response: ApiResponse<string> = await mediaObjectApi.preCreateMo(requestData);
    if (response.code === "error") {
      throw response;
    }
    return response.data;
  }

  /**
   * Function to update a media's file.
   *
   * @param mediaUuid - UUID of the media.
   * @param file - The new file to be updated to.
   * @returns {Promise}
   */
  async saveMediaFile(mediaUuid, fileResponse) {
    const requestData = {
      uuid: mediaUuid,
      fileEntry: {
        fileUuid: fileResponse.uuid,
        filename: fileResponse.name,
      },
      finalize: false,
    };
    const response = await mediaObjectApi.saveMediaObject(requestData);
    if (response.code === "error") {
      throw response;
    }
    return response.data;
  }

  // retrieveContext() {
  //   return new Promise(resolve => {
  //     if (!this.context) {
  //       this.getContext().then(context => {
  //         this.context = context;
  //         resolve(this.context);
  //       });
  //     } else {
  //       resolve(this.context);
  //     }
  //   });
  // }

  /**
   * Function to retrieve and return MO's meta data when the file is ready.
   * Retry is required as meta data may not be complete while the media file is still processing.
   * @param uuid
   * @returns {Promise}
   */
  async getMetaDataWhenFileReady(uuid) {
    const maxRetries = 20;
    const retryInterval = 3000;
    return CommonUtils.retryPromise(
      uuid,
      this.getMetaDataWhenFileReadyApi,
      maxRetries,
      retryInterval,
    );
  }

  /**
   * Function to retrieve a MO's meta data when the file is ready.
   * @param uuid
   * @returns {Promise}
   */
  async getMetaDataWhenFileReadyApi(uuid) {
    const response = await mediaObjectApi.getMetaData({ uuid: uuid });
    if (response.code !== "ok") {
      throw new Error("Response not ok");
    }
    const metaData = response.data;
    if (!isMediaObjectFileReady(metaData.fileStatus, metaData.fileType)) {
      throw new Error("File not ready");
    }
    return metaData;
  }

  async getMetaDataWhenFileProcessed(uuid) {
    const maxRetries = 20;
    const retryInterval = 3000;
    return CommonUtils.retryPromise(
      uuid,
      this.getMetaDataWhenFileProcessedStatusApi,
      maxRetries,
      retryInterval,
    );
  }

  async getMetaDataWhenFileProcessedStatusApi(uuid) {
    const response = await mediaObjectApi.getMetaData({ uuid: uuid });
    if (response.code !== "ok") {
      throw new Error("Response not ok");
    }
    const metaData = response.data;
    const fileStatus = metaData.fileStatus?.toUpperCase();
    if (
      fileStatus === MEDIA_OBJECT_FILE_STATUS.PROCESSING ||
      fileStatus === MEDIA_OBJECT_FILE_STATUS.UPLOADING ||
      fileStatus === MEDIA_OBJECT_FILE_STATUS.SCANNING
    ) {
      throw new Error("File still processing");
    }
    return metaData;
  }

  getType() {
    return "mo";
  }

  getFileExtension(fileNameWithExt) {
    return fileNameWithExt.split(".").pop();
  }

  async getImageBlob(imagePath) {
    try {
      return await mediaObjectApi.getMoImageBlob(imagePath);
    } catch {
      return this.passiveApiFailHandler();
    }
  }
}
